import useGetRewardsInfo from "@hooks/useGetRewardsInfo";
import logger from "@utils/logger";
import { WalletTxMethod } from "kz-ui-sdk";
import { useEffect, useMemo, useRef } from "react";

const CASHBACK_TYPES = [
  WalletTxMethod.InstantCashBack,
  WalletTxMethod.DailyCashBack,
  WalletTxMethod.WeeklyCashBack,
  WalletTxMethod.MonthlyCashBack,
] as const;

// Skip timeout refetch if the remaining seconds is larger than this value
// If the remaining seconds is too far away from the current time, it's not worth running the timer
const MAX_TIMEOUT_SECONDS = 30 * 60; // 30 minutes

/**
 * Refetch cashback status on timeout.
 */
export const CashbackRefetchOnTimeout = () => {
  const { data, refetch } = useGetRewardsInfo();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Find the earliest timeout to refetch
  // Skip invalid cashbacks
  const earliestCashback = useMemo(() => {
    if (!data) return null;

    const validCashbacks = CASHBACK_TYPES.map((type) => {
      const rewardInfo = data[type];

      // Skip if no remaining seconds or already expired
      if (!rewardInfo?.remainingSeconds || rewardInfo.remainingSeconds <= 0) {
        return null;
      }

      // Skip if claimPoints is 0 (not claimable)
      if (rewardInfo.claimPoints === 0) {
        return null;
      }

      // Skip if claimPoints is less than minClaimPoints (not claimable)
      if (
        rewardInfo.minClaimPoints !== undefined &&
        rewardInfo.claimPoints !== undefined &&
        rewardInfo.claimPoints < rewardInfo.minClaimPoints
      ) {
        return null;
      }

      return {
        type,
        remainingSeconds: rewardInfo.remainingSeconds,
        claimPoints: rewardInfo.claimPoints,
        minClaimPoints: rewardInfo.minClaimPoints,
      };
    }).filter((cashback) => !!cashback);

    if (validCashbacks.length === 0) return null;

    const earliest = validCashbacks.reduce((prev, current) =>
      (current?.remainingSeconds ?? 0) < (prev?.remainingSeconds ?? 0) ? current : prev,
    );

    return earliest;
  }, [data]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Set up the timeout to refetch the cashback status
   * Needn't to create timer for all cashback types, only create timer for the earliest timeout
   * After the earliest timeout, the next timeout will be the earliest timeout
   */
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!earliestCashback || earliestCashback.remainingSeconds <= 0) {
      return;
    }

    const { type, remainingSeconds, claimPoints, minClaimPoints } = earliestCashback;

    if (remainingSeconds > MAX_TIMEOUT_SECONDS) {
      logger._console.log(
        `[Cashback] Earliest timeout is too far away (${remainingSeconds} seconds > ${MAX_TIMEOUT_SECONDS} seconds), skipping timeout`,
        {
          type,
          remainingSeconds,
          claimPoints,
          minClaimPoints,
        },
      );
      return;
    }

    logger._console.log("[Cashback] Found earliest timeout, will refetch in", remainingSeconds, "seconds", {
      type,
      remainingSeconds,
      claimPoints,
      minClaimPoints,
    });
    timeoutRef.current = setTimeout(() => {
      // Prevent refetching if the component is unmounted
      if (!isMountedRef.current) {
        return;
      }
      logger._console.log("[Cashback] Refetching cashback status");
      refetch();
    }, remainingSeconds * 1000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [earliestCashback, refetch]);

  return null;
};
